/* eslint-disable no-console */
'use client';

import { useFeatureIsOn, useFeatureValue } from '@growthbook/growthbook-react';
import { useUserAgent } from 'hooks/useUserAgent';
import { analytics, getWebviewAnalyticsId } from 'lib/analytics';
import { PropsWithChildren, useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import { UserData } from 'stores/AuthStore';

const MIXPANEL_TOKEN = process.env.NEXT_PUBLIC_MIXPANEL_TOKEN || '';
const FEATURE_FLAG_ID = 'discovery-next.mixpanel';
type Platform = 'WEB' | 'APP';

const getDistinctId = (
  platform: Platform,
  isAndroid: boolean,
  isIos: boolean
): string => {
  if (platform !== 'APP') return '';
  const device = isAndroid ? 'ANDROID' : isIos ? 'IOS' : 'APP';
  return getWebviewAnalyticsId(device) || '';
};

export function AnalyticsProvider({
  children,
  dataUser
}: PropsWithChildren<{ dataUser?: UserData['dataUser'] }>) {
  const isEnabled = useFeatureIsOn(FEATURE_FLAG_ID);
  const { recordSessionPercent } = useFeatureValue(FEATURE_FLAG_ID, {
    recordSessionPercent: 0
  });
  const { isWebView, isAndroid, isIos } = useUserAgent();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (analytics.isInitialized) return;

    analytics.init(MIXPANEL_TOKEN, {
      debug: process.env.NODE_ENV !== 'production',
      persistence: 'localStorage',
      autocapture: false,
      record_heatmap_data: true,
      record_idle_timeout_ms: 300000,
      record_sessions_percent: recordSessionPercent,
      isEnabled
    });

    const owner =
      (searchParams?.get('owner') as 'bileto' | 'platform') || 'platform';
    const platform: Platform = isWebView ? 'APP' : 'WEB';
    const distinctId = getDistinctId(platform, isAndroid, isIos);
    analytics.register?.({
      product_channel: owner === 'bileto' ? 'BILETO' : 'DIY',
      platform,
      app_name: 'discovery-next',
      ...(distinctId && { distinct_id: distinctId })
    });

    if (dataUser?.id) {
      analytics.identifyUser?.({
        id: dataUser?.id,
        name: dataUser?.fullName,
        email: dataUser?.email,
        phone: dataUser?.phoneHome,
        birthdate: dataUser?.billing?.birthdate,
        city: dataUser?.billing?.addressCity,
        state: dataUser?.billing?.addressState
      });
    }

    return () => {
      analytics.cleanup();
    };
  }, [isEnabled, recordSessionPercent, dataUser, isWebView]);

  return <>{children}</>;
}
